<template>
  <v-form
  class="pa-6"
    ref="form"
    v-model="valid"
    lazy-validation>

    <h3 class="text-left mb-1 text-primary">Test GUI interface</h3>
    <p class="text-caption mb-4">Configurá y montá el widget de AlisonGUI.</p>
    <div class="d-flex ga-2 mb-6">
      <v-btn
        size="small"
        :disabled="!valid || (!desktop.enabled && !server.enabled  && !tad.enabled && !file.enabled)"
        color="primary"
        @click="mountAlisonGui">
        Mount >>
      </v-btn>
      <v-btn
        size="small"
        color="primary"
        @click="unmountAlisonGui">
        Unmount
      </v-btn>
    </div>

    <v-tabs v-model="activeTab">
      <v-tab value="general">General</v-tab>
      <v-tab value="providers">Providers</v-tab>
      <v-tab value="certificate">Certificate</v-tab>
      <v-tab value="style">Style</v-tab>
    </v-tabs>

    <v-tabs-window v-model="activeTab" class="mt-4">

      <!-- GENERAL -->
      <v-tabs-window-item value="general">
        <v-row>
          <v-col md="6">
            <v-select v-model="mode"
                :items="['select', 'sign', 'test', 'renew', 'authenticate', 'detect', 'detect-and-install']"
                label="Action"
                required
                @update:modelValue="updateRequest(mode,'mode')"
            />
            <v-select v-model="showText2SignFormat"
                :items="['',
                'text', 'text:compact','text:regular','text:wide',
                'html', 'html:compact','html:regular','html:wide',
                'base64', 'base64:compact','base64:regular','base64:wide']"
                label="TextFormat"
                required
                @update:modelValue="updateRequest(showText2SignFormat,'gui','showText2SignFormat')"
            />
            <template v-if="isSigningMode">
              <v-autocomplete v-model="algorithm"
                  :items="algorithmOPTIONS"
                  label="Algorithm"
              />
              <v-select v-model="format"
                  :items="formatItems"
                  label="Format"
              />
            </template>
          </v-col>
          <v-col md="6">
            <v-textarea v-model="textToSign"
              label="Text to sign"
              height="150"
              required
              @change="updateRequest([textToSign],'signature','dataToSign')"
            />
            <v-text-field v-if="format === 'PKCS1'"
              v-model="pkcs1Hash"
              label="Hashed data to sign (PKCS1)"
              readonly
            />
          </v-col>
        </v-row>
      </v-tabs-window-item>

      <!-- PROVIDERS -->
      <v-tabs-window-item value="providers">
        <v-row>
          <v-col cols="12" class="d-flex">
            <v-checkbox  v-model="desktop.enabled"
              class="mx-5 pt-0"
              label="Desktop"
            />
            <v-checkbox v-model="server.enabled"
              class="mx-5 pt-0"
              label="Server"
            />
            <v-checkbox v-model="tad.enabled"
              class="mx-5 pt-0"
              label="TAD"
            />
            <v-checkbox v-model="file.enabled"
              class="mx-5 pt-0"
              label="File"
            />
            <v-checkbox v-model="simulateOnValidate"
              class="mx-5 pt-0"
              label="Simular onValidate"
              :disabled="!server.enabled && !tad.enabled"
            />
          </v-col>

          <!-- TAD -->
          <v-col md="6" :class="{ hidden: !tad.enabled }">
            <h4 class="text-start mb-2">TAD Provider:</h4>
            <v-row justify="center">
              <v-text-field v-model="tad.seat"
                label="seat"
              />
              <v-btn class="mx-2" size="x-small" color="primary" @click="genTadServerToken()">
                Get Tad Token
              </v-btn>
            </v-row>
            <v-text-field v-model="tad.tenant"
              label="tenant"
            />
            <v-text-field v-model="tad.url"
              label="server URL"
            />
            <v-text-field v-model="tad.client_key"
              label="clientID"
            />
            <v-text-field v-model="tad.client_secret"
              label="clientSecret" type="password"
            />
          </v-col>

          <!-- SERVER -->
          <v-col md="6" :class="{ hidden: !server.enabled }">
            <h4 class="text-start mb-2">Server Provider:</h4>
            <v-row justify="center">
              <v-text-field v-model="server.seat"
                label="seat"
              />
              <v-btn class="mx-2" size="x-small" color="primary" @click="genAlisonServerToken()">
                Get Server Token
              </v-btn>
            </v-row>
            <v-text-field v-model="server.tenant"
              label="tenant"
            />
            <v-text-field v-model="server.url"
              label="server URL"
            />
            <v-text-field v-model="server.client_key"
              label="clientID"
            />
            <v-text-field v-model="server.client_secret"
              label="clientSecret" type="password"
            />
          </v-col>

          <!-- FILE -->
          <v-col md="6" :class="{ hidden: !file.enabled }">
            <h4 class="text-start mb-2">File Provider:</h4>
            <!-- fileB64 y password son opcionales: si quedan vacios el widget
                 muestra su propio formulario de carga del .p12 (el provider
                 tira FILE_INPUT_REQUIRED al inicializar). -->
            <v-file-input v-model="file.pick"
              label="PKCS#12 (.p12/.pfx)"
              accept=".p12,.pfx"
              variant="outlined"
              density="compact"
              prepend-icon=""
              @update:modelValue="readPickedFile"
            />
            <v-text-field v-model="file.fileName"
              label="fileName"
              @change="updateFileProvider()"
            />
            <v-text-field v-model="file.password"
              label="password" type="password"
              @change="updateFileProvider()"
            />
            <v-textarea v-model="file.fileB64"
              label="fileB64"
              rows="3"
              no-resize
              @change="updateFileProvider()"
            />
            <v-btn class="mt-2" size="x-small" color="primary" @click="clearFile()">
              Clear file
            </v-btn>
          </v-col>
        </v-row>
      </v-tabs-window-item>

      <!-- CERTIFICATE -->
      <v-tabs-window-item value="certificate">
        <v-row>
          <v-col md="6">
            <h4 class="text-start mb-2">Selector:</h4>
            <v-text-field
              v-model="selector.thumbPrint"
              label="ThumbPrint"
              @change="updateRequest(selector.thumbPrint,'selector','thumbPrint')"
            ></v-text-field>
            <v-text-field
              v-model="selector.profileName"
              label="profileName"
              @change="updateRequest(selector.profileName,'selector','profileName')"
            ></v-text-field>
            <v-text-field
              v-model="selector.keyStoreId"
              label="keyStoreId"
              @change="updateRequest(selector.profileName,'selector','keyStoreId')"
            ></v-text-field>
          </v-col>
          <v-col md="6">
            <h4 class="text-start mb-2">Filters:</h4>
            <div class="my-2">
                 <v-btn class="mx-2" size="x-small" color="primary" @click="dialogFilter=!dialogFilter">
                    Add filter
                  </v-btn>
                 <v-btn class="mx-2" size="x-small" color="primary" @click="filterRequest='[]'">
                    Clean filters
                  </v-btn>
            </div>
            <v-textarea
              v-model="filterRequest"
              no-resize
              height="250"
              label="Filters">
            </v-textarea>
          </v-col>
        </v-row>
      </v-tabs-window-item>

      <!-- STYLE -->
      <v-tabs-window-item value="style">
        <v-row>
          <v-col md="6">
            <h4 class="text-start mb-2">Theme:</h4>
            <v-radio-group v-model="theme" row>
              <v-radio
                v-for="n in 3"
                :key="n"
                :label="`Theme ${n}`"
                :value="n"
              />
            </v-radio-group>
          </v-col>
          <v-col md="6">
            <h4 class="text-start mb-2">Palette:</h4>
            <div class="my-2">
                <v-btn class="mx-2" size="x-small" color="primary"
                       @click="setPalette()">
                    Set Palette
                </v-btn>
                <v-btn class="mx-2" size="x-small" color="primary" @click="unsetPalette()">
                    UnSet Palette
                </v-btn>
            </div>
            <v-textarea v-model="palette"
                    no-resize
                    height="350"
                    label="Palette">
            </v-textarea>
          </v-col>
        </v-row>
      </v-tabs-window-item>

    </v-tabs-window>

    <v-divider class="my-6"></v-divider>

    <v-row>
      <v-col cols="12">
        <v-textarea
                v-model="alisonGUIargs"
                label="Parameters"
                required
                height="300"
        ></v-textarea>
      </v-col>
    </v-row>

    <v-divider class="my-6"></v-divider>

    <v-row>
      <v-col cols="12">
        <v-textarea
          v-model="operationResult"
          auto-grow
          label="Result">
        </v-textarea>
      </v-col>
    </v-row>

    <v-divider class="my-6"></v-divider>

    <v-row>
      <v-col cols="12">
        <h4 class="text-start mb-2">Validar con Debbie</h4>
      </v-col>
      <v-col md="2">
        <v-select v-model="debbie.environment"
          :items="['desa', 'homo', 'prod']"
          label="Ambiente"
        />
      </v-col>
      <v-col md="3">
        <v-select v-model="debbie.signatureType"
          :items="formatItems"
          label="signatureType"
        />
      </v-col>
      <v-col md="2">
        <v-select v-model="debbie.signedTextPack"
          :items="[{ title: '(raw)', value: '' }, { title: 'BASE64', value: 'BASE64' }]"
          label="signedTextPack"
        />
      </v-col>
      <v-col md="3">
        <v-text-field v-model="debbie.policyName"
          label="Policy"
        />
      </v-col>
      <v-col md="2" class="d-flex align-center">
        <v-btn
          size="small"
          color="primary"
          :disabled="!lastSignResult || !debbie.policyName"
          @click="validateWithDebbie">
          Validar
        </v-btn>
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="debbieResult"
          auto-grow
          label="Resultado Debbie">
        </v-textarea>
      </v-col>
    </v-row>
     <v-dialog
      v-model="dialogFilter"
      width="500"
      persistent
    >
      <!-- variant flat + border: el default global de VCard es "outlined", que en
           Vuetify 3 deja el fondo transparente y deja ver la página de atrás. -->
      <v-card variant="flat" border>
        <v-card-title
          class="headline primary"
        >
          Filter
        </v-card-title>


        <v-form class="ma-5">
                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="issuer"
                      label="Issuer"
                    ></v-text-field>
                  </v-col>
                  <v-col>
                    <v-text-field
                      v-model="subject"
                      label="Subject"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-select
                  v-model="keystoreFilter"
                  :menu-props="{  maxHeight: 204 }"
                  :items="['WIN-ENH','CSK','ETOKEN','MTOKEN','MACOS','FF-NSS','PFX','CLOUD']"
                  attach
                  label="keystores"
                  multiple
                ></v-select>
                <v-row class="align-start">
                  <v-col cols="4" class="text-start">
                    <p style="color: rgba(0, 0, 0, 0.6);">windowValidity:</p>
                  </v-col>
                  <v-col cols="2" class="pa-0">
                    <v-text-field
                      v-model="windowValidity.since"
                      class="mt-0 pt-0"
                      single-line
                      :rules="[v => /^\d*\**\-?\d*$/.test(v) || 'value not alowed']"
                      type="text"
                      style="width: 60px"
                      @change="checkValue()"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="1"  style="color: rgba(0, 0, 0, 0.6);">to</v-col>
                  <v-col cols="2" class="pa-0">
                    <v-text-field
                      v-model="windowValidity.until"
                      class="mt-0 pt-0"
                      single-line
                      :rules="[v => /^\d*\*?\d*$/.test(v) || 'value not alowed']"
                      type="text"
                      style="width: 60px"
                      @change="checkValue()"
                    ></v-text-field>
                  </v-col>
                  <v-col>
                  </v-col>
                </v-row>
            <v-row>
                <v-col>
                    <v-text-field
                            v-model="status"
                            label="Status"
                    ></v-text-field>
                </v-col>
            </v-row>
        </v-form>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            size="small"
            color="primary"
            text
            @click="dialogFilter=false">Cancel
          </v-btn>
          <v-btn
            size="small"
            color="primary"
            text
            @click="addFilter()"
          >
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo propio, fuera del sistema de overlays de Vuetify (v-dialog
         teleporta a un contenedor global y calcula su z-index de forma
         relativa al último overlay activo — snackbar, tooltip, un select —
         lo que puede dejarlo por debajo del host de AlisonGUI aunque se le
         pase :z-index explícito). Un div fijo con z-index propio no depende
         de ese cálculo: siempre gana contra el 999999 que AlisonGUI le fija
         a su host mientras el modal está abierto (ver alison-gui/src/index.jsx). -->
    <div v-if="identityDialogOpen" class="identity-verify-overlay">
      <div class="identity-verify-overlay__scrim"></div>
      <div class="identity-verify-overlay__content">
        <VerifyIdentityDialog
          @cancel="handleIdentityCancel"
          @success="handleIdentitySuccess"
          @error="handleIdentityError"
        />
      </div>
    </div>
  </v-form>
</template>
<script>
  import * as testingData from '../datas/testingDatas.js'
  import palette1def from '../assets/palette1.json';

import { mapMutations } from 'vuex'
import alisonGUI from  '../services/alison_gui.js'
import debbie from '../services/debbie.js'
  import enviroment from "@/enviroment";
  import VerifyIdentityDialog from './VerifyIdentityDialog.vue'

  // PKCS1 firma en crudo: el desktop no calcula el hash del documento (a
  // diferencia de CAdES/XAdES), así que hay que pre-hashearlo acá con el
  // algoritmo elegido antes de mandarlo como dataToSign.
  const HASH_ALGORITHMS = {
    SHA1withRSA: 'SHA-1',
    SHA256withRSA: 'SHA-256',
    SHA512withRSA: 'SHA-512',
  }

  export default  {
    props: ['certList'],
    components: {
      VerifyIdentityDialog,
    },
    data: () => ({
      activeTab: 'general',
      mode: 'select',
      selector:{
        thumbPrint: "",
        profileName: "",
        keyStoreId: ""
      },
			desktop: {
				enabled: true,
				urls: ['https://127.0.0.1:8004','https://localhost:8004','http://127.0.0.1:8003'],
				token: "eyJ2ZXJzaW9uIjo0LCJ2MSI6eyJpZHgiOiI5LUxpY2Vuc2UiLCJ2YWx1ZSI6IjMvK0RKMmsxQS9yWjQxL0hlWllYS00ydG1XazE4cDM4RXg0WDdWTWZlYXhpS3dWUHZVRTZzaUh1aHplZThHU1RMR2FwUWRXaFJRNzlyQXRqeWpRWkYxeC83Rm1ZcERzVExnTTQzdDRlV2dMV2NaclQ2Sy9uRGwvc1ltRGtLZjBjWDFtc3JHbkM4RWNQNk9KQW04RGlhUG5ZVlR0ZUpaOVEzbVNJeDk2OGU0cnpJNlF6YUh2L09FQnVmUHhMbTdpMjhQS3JBbHdSS3BQZTFVc3c4eG45NjMxRlJWc1JxaEk1WGFoamRoV2RWQnRkRmg1bE51Y1BUUDVEbFQzdkJIZy94VmV5TllOdVcvRWFobUZiS2RSRS9IRmdEOFN0cnVPTTVvNXJLSHptc2xPbnJFSi8yQkdEa1AxcnF3Nm0wZnlZUTR6K2RpYzdlTHhLWnRsNUNGWGhBR0dLOEdIYXVJVGU5RWUrQUJjN3c1WC8zZkJKVDI4VTgyOEo1V0dOZ0t6TVo5akdsbzVLNVgxOEh1cHVvZ1BGSm9nVjkyV2llbmR5c2JXMmRiZXltcVNINDBTN2kvcDdLb2pRZ2ZyNWtveHl1ZFV1cmgvK1NuYXhYOEczMTF2THU0OUNIalpmdCtycXkvRFlUZFdRS3pYYTFHanZMME5zQkIyNTFRU01OdDdoLzFSYjJkdnNMVk5HcnNKR3BjNFhMWEx1a0J6V1pXSW4zeWd1cDZUMlR0SnhzZCtPSWhtVkxhZldNbHhNME03diJ9LCJ2MiI6eyJpZHgiOiI5LUxpY2Vuc2UiLCJ2YWx1ZSI6ImV5SmhiR2NpT2lKU1V6STFOaUo5LmV5SnBZWFFpT2pFM05EUXpNVEkyTURVc0ltVjRjQ0k2TVRnME56Y3pOakF3TUN3aVkyOXRjR0Z1ZVU1aGJXVWlPaUpEWlhKMGFWTjFjaUJUTGtFdUlpd2lZV3hzYjNkbFpFOXlhV2RwYm5NaU9sc2lLaTVqWlhKMGFYTjFjaTVqYjIwaUxDSXFMbU5sY25ScGMzVnlMbTVsZENJc0lpb3VZMlZ5ZEdsemRYSXVhVzVtYnlJc0lpb3VkbVZ5YVhOMWNpSXNJaW91ZEc5d2RISjFjM1F1WTI5dExtRnlJaXdpS2k1elpXTjFjbVZrYjJNdWJHRWlMQ0pqYUhKdmJXVXRaWGgwWlc1emFXOXVPaTh2YTNCcWJHRm5aR3B3WVdscGJXNWxZMlJpWkdOdVpHcGphV3B2WVd4aWJHRWlMQ0l4TWpjdU1DNHdMakU2T0RBd015SXNJakV5Tnk0d0xqQXVNVG80TURBMElpd2liRzlqWVd4b2IzTjBPamd3TURNaUxDSnNiMk5oYkdodmMzUTZPREF3TkNJc0lteHZZMkZzYUc5emRDNWpaWEowYVhOMWNpNXVaWFE2T0RBNE1DSmRmUS5Gc09hel9LRWJ2WFh1dkZldXVCWExNYU42dVNjcXF2akt2Vl94dnhrUjQ2eGxuMDZyWnhTby14NElHdjcxajN0dHpERWVXUkQzUTZyV1lfeTViajR3LS0wN1dGRnM4eE8zdVpDUVdfclh3enk5TTJ2bkxIRGdtc0IyNUFPYkhTUVY3QW9iNzRZYTJRcjdlMVFQZldGallpUjlMV3hlVGV0a2dpWTluV2U2MFRoNG5sZC1mbGJSbjVVVnExQ0hqTVBiR3llc0hmSktXSkxZN001NlRIN0pKTno2Q0FNTFIwQkRlSmpodUhJN2ktMkJ2MjRzMmREMFV4Q3ZXU1RJM1piODkyc2xQMVBFdHROWGpvU2tVWVotSlU0WU1GN2tSVDZabnZmR3ZYSjdPQXdnTTFvMFRjR0Q2NGt0bXVySk00dzJoVGZXUDd3SGxXNjFoNE1aamVRcmcifX0="
      },
      server:{
				enabled: false,
	      token: null,
        client_key: enviroment.alisonServerUseClientKey,
        client_secret: enviroment.alisonServerUseClientSecret,
        seat: enviroment.alisonServerSeat,
        tenant: enviroment.alisonServerTenant,
        url: enviroment.alisonServerApiUrl
      },
      file:{
        enabled: false,
        fileName: "",
        fileB64: "",
        password: "",
        pick: null
      },
      tad:{
				enabled: true,
	      token: null,
        client_key: enviroment.tadServerUseClientKey,
        client_secret: enviroment.tadServerUseClientSecret,
        seat: enviroment.tadServerSeat,
        tenant: enviroment.tadServerTenant,
        url: enviroment.tadServerApiUrl
      },
      valid: true,
      simulateOnValidate: false,
      theme: 3,
      dialogFilter: false,
      errorMessages: '',
      windowValidity:{since:'*', until:'*'},
      requestPayload: '{}',
      operationResult:'',
	    showText2SignFormat: "text",
      algorithm: "SHA256withRSA",
      format: "CAdES",
      algorithmOPTIONS: [],
      formatItems: [],
      pkcs1Hash: '',
      textToSign: "PGgzIHN0eWxlPSJjb2xvcjogIzMzMzMzMzsgZm9udC13ZWlnaHQ6IGJvbGQiPkRhdG9zIGRlIGxhIFRyYW5zZmVyZW5jaWE8L2gzPgoKPHRhYmxlIHN0eWxlPSJib3JkZXI6IDFweCBzb2xpZCAjY2NjY2NjOyB3aWR0aDogMTAwJTsgYm9yZGVyLWNvbGxhcHNlOiBjb2xsYXBzZSI+CgogIDx0ciBzdHlsZT0iYmFja2dyb3VuZC1jb2xvcjogI2Y1ZjVmNSI+CgogICAgPHRoIHN0eWxlPSJ0ZXh0LWFsaWduOiBsZWZ0OyBwYWRkaW5nOiA2cHg7IGJvcmRlcjogMXB4IHNvbGlkICNjY2NjY2MiPkNhbXBvPC90aD4KCiAgICA8dGggc3R5bGU9InRleHQtYWxpZ246IGxlZnQ7IHBhZGRpbmc6IDZweDsgYm9yZGVyOiAxcHggc29saWQgI2NjY2NjYyI+VmFsb3I8L3RoPgoKICA8L3RyPgoKICA8dHI+CgogICAgPHRkIHN0eWxlPSJwYWRkaW5nOiA2cHg7IGJvcmRlcjogMXB4IHNvbGlkICNjY2NjY2MiPjxiPkN1ZW50YSBvcmlnZW48L2I+PC90ZD4KCiAgICA8dGQgc3R5bGU9InBhZGRpbmc6IDZweDsgYm9yZGVyOiAxcHggc29saWQgI2NjY2NjYzsgd2hpdGUtc3BhY2U6IG5vd3JhcCI+MC0wMDAtMDEyMzQ1NjctODwvdGQ+CgogIDwvdHI+CgogIDx0cj4KCiAgICA8dGQgc3R5bGU9InBhZGRpbmc6IDZweDsgYm9yZGVyOiAxcHggc29saWQgI2NjY2NjYyI+PGI+TW9udG88L2I+PC90ZD4KCiAgICA8dGQgc3R5bGU9InBhZGRpbmc6IDZweDsgYm9yZGVyOiAxcHggc29saWQgI2NjY2NjYzsgd2hpdGUtc3BhY2U6IG5vd3JhcCI+JCAxLjUwMC4wMDA8L3RkPgoKICA8L3RyPgoKICA8dHI+CgogICAgPHRkIHN0eWxlPSJwYWRkaW5nOiA2cHg7IGJvcmRlcjogMXB4IHNvbGlkICNjY2NjY2MiPjxiPkJlbmVmaWNpYXJpbzwvYj48L3RkPgoKICAgIDx0ZCBzdHlsZT0icGFkZGluZzogNnB4OyBib3JkZXI6IDFweCBzb2xpZCAjY2NjY2NjIj5KdWFuIFAmZWFjdXRlO3JleiBMJm9hY3V0ZTtwZXo8L3RkPgoKICA8L3RyPgoKPC90YWJsZT4KCjxicj4KCjxwIHN0eWxlPSJjb2xvcjogI2NjMDAwMCI+PGVtPkF1dG9yaXpvIGxhIG9wZXJhY2nDs24gaW5kaWNhZGEgYXJyaWJhIGFsIGZpcm1hciBkaWdpdGFsbWVudGUgZXN0YSBzb2xpY2l0dWQuPC9lbT48L3A+",
      subject: '',
      issuer: '',
	    status: 'valid,expired,revoked',
      filterRequest: '[]',
      keystoreFilter: [],
      cuil:'',
        palette: null,
      alisonGUIargs: '{}',
      identityDialogOpen: false,
      identityValidateResolve: null,
      identityValidateReject: null,
      lastSignResult: null,
      debbie: {
        environment: 'desa',
        signatureType: 'CAdES',
        signedTextPack: 'BASE64',
        policyName: 'any-any',
      },
      debbieResult: '',
    }),
    computed: {
      isSigningMode(){
        return ['sign', 'test', 'renew', 'authenticate'].includes(this.mode)
      }
    },
    watch: {
      algorithm(value){
        let request = JSON.parse(this.alisonGUIargs)
        request.signature.options.algorithm = value
        this.alisonGUIargs = JSON.stringify(request,null, "\t")
        this.updatePkcs1Hash()
      },
      format(value){
        let request = JSON.parse(this.alisonGUIargs)
        request.signature.options.format = value
        this.alisonGUIargs = JSON.stringify(request,null, "\t")
        this.updatePkcs1Hash()
      },
      textToSign(){
        this.updatePkcs1Hash()
      },
      filterRequest(value){
        this.updateRequest(JSON.parse(value),'filters')
      },
      theme(value){
          let name = { name: 'theme'+ value }
          this.updateRequest(name,'gui','theme');
      },
      'server.enabled'(value) {
        let request = JSON.parse(this.alisonGUIargs)
        if (value) request.providers.server = this.getServerProvider()
        else delete(request.providers.server);
        this.alisonGUIargs = JSON.stringify(request,null, "\t")
      },
      'tad.enabled'(value) {
        let request = JSON.parse(this.alisonGUIargs)
        if (value) request.providers.tad = this.getTadProvider()
        else delete(request.providers.tad);
        this.alisonGUIargs = JSON.stringify(request,null, "\t")
      },
      'file.enabled'(value) {
        let request = JSON.parse(this.alisonGUIargs)
        if (value) request.providers.file = this.getFileProvider()
        else delete(request.providers.file);
        this.alisonGUIargs = JSON.stringify(request,null, "\t")
      },
      'desktop.enabled'(value) {
        let request = JSON.parse(this.alisonGUIargs)
        if (value) request.providers.desktop = this.getDesktopProvider()
        else delete(request.providers.desktop);
        this.alisonGUIargs = JSON.stringify(request,null, "\t")
      },
      // Los selects propios del diálogo (Tipo de documento/Sexo) abren un
      // v-menu de Vuetify que teleporta al contenedor global de overlays y
      // calcula su z-index sin relación al nuestro (el diálogo ya no
      // participa del stack de Vuetify — ver .identity-verify-overlay en
      // global.css). Sin esto, ese menú queda oculto detrás del host de
      // AlisonGUI aunque el diálogo en sí se vea bien.
      identityDialogOpen(value) {
        document.body.classList.toggle('identity-verify-active', value);
      },
    },
    beforeUnmount() {
      // Si se navega a otra tab con el diálogo abierto, el watcher de arriba
      // nunca llega a correr con value=false — sin esto, la clase queda
      // pegada en <body> y afecta el z-index de overlays de Vuetify en el
      // resto de la app.
      document.body.classList.remove('identity-verify-active');
    },
    mounted() {
      let request = {
        gui: {
          theme: 'theme'+this.theme,
          logo: "./acme/logo.jpg",
          header: "",
          language: {
            key:'es',
            resources: null,
          },
	        showText2SignFormat: this.showText2SignFormat,
	        wizardUrl: "https://desa-desktop.certisur.net/acme"
        },
        mode: this.mode,
        providers: {
          desktop: {
						urls: this.desktop.urls,
            accessToken: this.desktop.token
          },
        },
        signature: {
          dataToSign: [this.textToSign],
          options: {
            algorithm: this.algorithm,
            format: this.format,
            params: ''
          }
        },
        selector: this.selector,
        filters: JSON.parse(this.filterRequest)
      }
        this.algorithmOPTIONS = Object.keys(testingData.algorithmOPTIONS)
        this.formatItems = Object.keys(testingData.signatureTypeOPTIONS)
        this.palette = JSON.stringify(palette1def,null, "\t")

        this.alisonGUIargs = JSON.stringify(request,null, "\t")
    },
    methods: {
      ...mapMutations({
        setSnack: 'setSnack',
        setColor: 'setColor',
	      showSnack: 'showSnack'
      }),

			getDesktopProvider() {
				return {
					accessToken: this.desktop.token,
					urls: this.desktop.urls
				}
			},
			getServerProvider() {
				return {
					token: this.server.token,
					tenant: this.server.tenant,
					userType: 'user',
					url: this.server.url
				}
			},
			getFileProvider() {
				// Solo se mandan fileB64/password si el usuario los cargo: si faltan,
				// el widget pide el archivo y la clave en su propio formulario.
				const provider = { fileName: this.file.fileName }
				if (this.file.fileB64) provider.fileB64 = this.file.fileB64
				if (this.file.password) provider.password = this.file.password
				return provider
			},
			getTadProvider() {
				return {
					token: this.tad.token,
					tenant: this.tad.tenant,
					url: this.tad.url
				}
			},
			async onValidateIdentity() {
				return new Promise((resolve, reject) => {
					this.identityValidateResolve = resolve;
					this.identityValidateReject = reject;
					this.identityDialogOpen = true;
				});
			},
			async handleIdentitySuccess() {
				// La identidad en sí se simula sin backend, pero una vez "validada"
				// intentamos obtener tokens reales de cada provider habilitado —
				// son los que AlisonGUI necesita de vuelta para poder listar
				// certificados reales.
				this.identityDialogOpen = false;
				try {
					const tokens = {};
					if (this.server.enabled) {
						this.server.token = await this.fetchProviderToken('server');
						tokens.serverToken = this.server.token;
					}
					if (this.tad.enabled) {
						this.tad.token = await this.fetchProviderToken('tad');
						tokens.tadToken = this.tad.token;
					}
					this.setColor('success');
					this.setSnack('Identidad validada, token real obtenido');
					this.identityValidateResolve?.(tokens);
				} catch (err) {
					this.setColor('error');
					this.setSnack('Identidad validada, pero falló la obtención del token real');
					this.operationResult = JSON.stringify(err, null, "\t");
					this.identityValidateReject?.(err);
				} finally {
					this.cleanupIdentityValidation();
				}
			},
			handleIdentityError(err) {
				// No rechazamos la Promise de onValidate: el diálogo queda abierto
				// para que se pueda reintentar, igual que en secure-doc-frontend.
				this.setColor('error');
				this.setSnack('Error simulado de validación de identidad');
				this.operationResult = JSON.stringify({ code: err.code, message: err.message }, null, "\t");
			},
			handleIdentityCancel() {
				this.identityDialogOpen = false;
				this.identityValidateReject?.(new Error('Identity validation cancelled'));
				this.cleanupIdentityValidation();
			},
			cleanupIdentityValidation() {
				this.identityValidateResolve = null;
				this.identityValidateReject = null;
			},

        setPalette() {
            let theme = {
                name: "theme"+this.theme,
                palette: JSON.parse(this.palette)
            }
            this.updateRequest(theme,'gui','theme');
        },
        unsetPalette() {
            let theme = {
                name: "theme"+this.theme
            }
            this.updateRequest(theme,'gui','theme');
        },

      updateFileProvider(){
        if (!this.file.enabled) return
        let request = JSON.parse(this.alisonGUIargs)
        request.providers.file = this.getFileProvider()
        this.alisonGUIargs = JSON.stringify(request,null, "\t")
      },
      async readPickedFile(picked){
        const pickedFile = Array.isArray(picked) ? picked[0] : picked
        if (!pickedFile) return
        try {
          const bytes = new Uint8Array(await pickedFile.arrayBuffer())
          // De a chunks: String.fromCharCode(...bytes) revienta el stack con
          // contenedores grandes.
          let binary = ''
          for (let i = 0; i < bytes.length; i += 8192) {
            binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192))
          }
          this.file.fileB64 = btoa(binary)
          this.file.fileName = pickedFile.name
          this.updateFileProvider()
        } catch (err) {
          this.setColor('error')
          this.setSnack('Error leyendo el archivo PKCS#12')
          this.operationResult = JSON.stringify(err.message,null, "\t");
        }
      },
      clearFile(){
        this.file.pick = null
        this.file.fileB64 = ""
        this.file.fileName = ""
        this.file.password = ""
        this.updateFileProvider()
      },
      changeCuil(value){
        let request = JSON.parse(this.alisonGUIargs);
        request.providers.tad = value;
        this.alisonGUIargs = JSON.stringify(request,null, "\t")
      },
      updateRequest(value,key,secundaryKey){
        let request = JSON.parse(this.alisonGUIargs);
        if(secundaryKey){
          request[key][secundaryKey] = value
        } else {
          request[key] = value
        }
        this.alisonGUIargs = JSON.stringify(request,null, "\t")
      },
        unmountAlisonGui () {
            alisonGUI.unmount();
        },
      async hashDataToSign(algorithm, base64Text){
        const hashName = HASH_ALGORITHMS[algorithm] || HASH_ALGORITHMS.SHA256withRSA
        const bytes = Uint8Array.from(atob(base64Text), c => c.charCodeAt(0))
        const digest = await crypto.subtle.digest(hashName, bytes)
        return btoa(String.fromCharCode(...new Uint8Array(digest)))
      },
      async updatePkcs1Hash(){
        if (this.format !== 'PKCS1') {
          this.pkcs1Hash = ''
          return
        }
        try {
          this.pkcs1Hash = await this.hashDataToSign(this.algorithm, this.textToSign)
        } catch (e) {
          this.pkcs1Hash = ''
        }
      },
      async mountAlisonGui () {
        if (this.$refs.form.validate()) {
            let request = JSON.parse(this.alisonGUIargs)
            if (this.format === 'PKCS1') {
              await this.updatePkcs1Hash()
              if (!this.pkcs1Hash) {
                this.setColor('error')
                this.setSnack('Error al hashear el texto a firmar')
                return
              }
              request.signature.dataToSign = [this.pkcs1Hash]
            }
            request.onConfirm = (result) => {
                this.setColor('success')
                this.setSnack('Signed successfully')
                this.operationResult = JSON.stringify(result,null, "\t");
                this.lastSignResult = (result && result.signatures && result.signatures.length) ? result : null;
                if (this.lastSignResult) this.debbie.signatureType = this.format;
                this.debbieResult = '';
            }
            request.onCancel = (result) => {
                this.setColor('error')
                this.setSnack('CANCEL')
                this.operationResult = JSON.stringify(result,null, "\t");
                console.log("CANCEL");
            }
            // onValidate se asigna acá y no en getServerProvider()/getTadProvider():
            // ese request viaja por el textarea de Parameters vía JSON.stringify/parse,
            // que descarta funciones, así que cualquier callback tiene que agregarse
            // recién sobre el objeto ya parseado, igual que onConfirm/onCancel arriba.
            if (this.simulateOnValidate) {
              if (request.providers.server) request.providers.server.onValidate = () => this.onValidateIdentity();
              if (request.providers.tad) request.providers.tad.onValidate = () => this.onValidateIdentity();
            }
            alisonGUI.mount(request);
        }
      },
      checkValue(){
        if(this.windowValidity.since==='') this.windowValidity.since ='*'
        if(this.windowValidity.until==='') this.windowValidity.until ='*'
      },
      addFilter(){
        const newFilter = {
              keyStores: this.keystoreFilter.length > 0 ? this.keystoreFilter : null,
              windowValidity: (this.windowValidity.since && this.windowValidity.until) ? this.windowValidity.since +","+this.windowValidity.until : null,
              subject: this.subject,
              issuer: this.issuer,
              status: this.status
            }
        let filters = JSON.parse(this.filterRequest)
        filters.push(newFilter)
        this.filterRequest = JSON.stringify(filters,null, "\t");
        this.dialogFilter = false
      },
      async fetchProviderToken(providerKey) {
        const provider = this[providerKey];
        const response = await alisonGUI.serverToken({
          client_id: provider.client_key,
          client_secret: provider.client_secret,
          grant_type: 'client_credentials',
          scope: `seat:${provider.seat}`
        });
        return response.access_token;
      },
      async genAlisonServerToken() {
        try {
              this.server.token = await this.fetchProviderToken('server');
              let request = JSON.parse(this.alisonGUIargs)
              this.setColor('success')
              this.setSnack('Generated access token')
	            request.providers.server = this.getServerProvider();
              this.alisonGUIargs = JSON.stringify(request,null, "\t")
          } catch (err) {
              this.setColor('error')
              this.setSnack('Error getting access token')
              this.operationResult = JSON.stringify(err,null, "\t");
          }
      },
      async genTadServerToken() {
        try {
					this.tad.token = await this.fetchProviderToken('tad');
					let request = JSON.parse(this.alisonGUIargs)
					this.setColor('success')
					this.setSnack('Generated TAD token')
					request.providers.tad = this.getTadProvider();
					this.alisonGUIargs = JSON.stringify(request,null, "\t")
				} catch (err) {
					this.setColor('error')
					this.setSnack('Error getting access token')
					this.operationResult = JSON.stringify(err,null, "\t");
        }
      },
      async validateWithDebbie() {
        try {
          const isPkcs1 = this.debbie.signatureType === 'PKCS1';
          // Verificado contra desa-validate.certisur.com con una firma PKCS1 real:
          // Alison Desktop hashea de nuevo lo que le pasamos como dataToSign, así
          // que lo que hay que mandarle a Debbie como signedText es el mismo
          // digest que se firmó (this.pkcs1Hash / lastSignResult.dataToSign[0]),
          // no el texto original — mandar el texto original da ERR_INVALID_SIGNATURE.
          const signedText = this.lastSignResult.dataToSign ? this.lastSignResult.dataToSign[0] : this.textToSign;
          const signature = this.lastSignResult.signatures[0];
          const params = {
            environment: this.debbie.environment,
            policyName: this.debbie.policyName,
            signatureType: this.debbie.signatureType,
            signedTextPack: this.debbie.signedTextPack,
            signedText,
            signature,
          }
          if (isPkcs1) {
            // DebbiePKCS1Request no tiene envelope CMS del que sacar el
            // certificado, así que hay que mandarlo aparte.
            const pemCertificate = this.lastSignResult.certificate?.cert?.pkcs7 || this.lastSignResult.certificate?.pkcs7;
            if (!pemCertificate) {
              this.setColor('error')
              this.setSnack('No se encontró el certificado del firmante en el resultado para validar PKCS1')
              return
            }
            // PKIUtils.sanitizeP7B reemplaza " " por "+", lo que corrompe el
            // header "-----BEGIN CERTIFICATE-----" (tiene un espacio) y hace
            // fallar el parseo (ERR_NONE_CERTIFICATE_FOUND) — mandamos solo
            // el base64 crudo, sin armor PEM, para esquivar ese bug.
            params.algorithm = this.algorithm
            params.certificate = pemCertificate.replace(/-----(BEGIN|END) CERTIFICATE-----/g, '').replace(/\s+/g, '')
          }
          const result = await debbie.validate(params)
          this.setColor('success')
          this.setSnack('Validación Debbie OK')
          this.debbieResult = JSON.stringify(result,null, "\t");
        } catch (err) {
          this.setColor('error')
          this.setSnack('Error validando en Debbie')
          this.debbieResult = JSON.stringify(err.response ? err.response.data : err.message,null, "\t");
        }
      },
    },
  }
</script>
<style scoped>
  .hidden{
    display:none;
  }
</style>
